import { useCallback, useEffect, useMemo, useState } from 'react';

export interface OnMoreDataEvent<R> {
	shouldReset: boolean;
	data: R;
}

interface PagingState {
	nextPage: number;
	loadingPage: number;
	hasMore: boolean;
}

export const useLoadMore = <R>(
	isReadyToLoad: boolean,
	fetch: (page: number) => Promise<R>,
	checkHasMore: (res: R) => boolean,
	onMoreData: (event: OnMoreDataEvent<R>) => void
) => {
	const [paging, setPaging] = useState<PagingState>({ loadingPage: -1, nextPage: 0, hasMore: true });

	const load = useCallback(async () => {
		if (paging.loadingPage >= paging.nextPage || !paging.hasMore || (paging.loadingPage >= 0 && !isReadyToLoad)) {
			return;
		}
		setPaging((p) => ({ ...p, loadingPage: p.nextPage }));
		const data = await fetch(paging.nextPage);
		setPaging((p) => ({ ...p, nextPage: p.nextPage + 1, hasMore: checkHasMore(data) }));
		onMoreData({ shouldReset: paging.nextPage === 0, data });
	}, [paging.loadingPage, paging.nextPage, paging.hasMore, isReadyToLoad, fetch, onMoreData, checkHasMore]);

	const reset = useCallback(() => {
		setPaging({ loadingPage: -1, nextPage: 0, hasMore: true });
	}, []);

	useEffect(() => {
		reset();
	}, [fetch, reset]);

	return useMemo(() => ({ load, reset, isLoading: paging.loadingPage === paging.nextPage }), [load, reset, paging.loadingPage, paging.nextPage]);
};
